import { AxiosError, AxiosResponse } from 'axios';
import { usePost } from 'hooks/use-post';
import React, { useContext, useState } from 'react';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { getScenarioAppliedFeedProgramsOverview, postScenarioApply } from 'views/bluviu-nutrition/features/scenarios-v2/api/scenario-apply';
import { R2ScenarioParams } from 'views/bluviu-nutrition/features/scenarios-v2/types/r2-scenario-params.type';
import { ApplyFeedFormInput, CageFeedProgram } from 'views/bluviu-nutrition/features/scenarios-v2/types/feed-mappings-input.type';
import { FeedMappingInput } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-feed-map';
import { AppliedScenario } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-applied.type';
import { ScenarioSubTab } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-sub-tab.enum';
import { useGet } from 'hooks/use-get';

type ScenarioFishGroupsState = {
  cageFeedPrograms: CageFeedProgram[];
  feedMappings: FeedMappingInput[];
  setCageFeedPrograms: (cageFeedPrograms: CageFeedProgram[]) => void;
  setFeedMappings: (feedMappings: FeedMappingInput[]) => void;
  applyMutation: UseMutationResult<AxiosResponse, AxiosError, ApplyFeedFormInput>;
  appliedScenarioQuery: UseQueryResult<AppliedScenario, AxiosError>;
};

const ScenarioFishGroupsContext = React.createContext<ScenarioFishGroupsState | undefined>(undefined);

type ScenarioFishGroupsProviderProps = {
  children: React.ReactNode;
};

const ScenarioFishGroupsProvider: React.FC<ScenarioFishGroupsProviderProps> = ({ children }) => {
  const params = useParams<R2ScenarioParams>();

  const [cageFeedPrograms, setCageFeedPrograms] = useState<CageFeedProgram[]>([]);
  const [feedMappings, setFeedMappings] = useState<FeedMappingInput[]>([]);

  const applyMutation = usePost<AxiosResponse, ApplyFeedFormInput, AxiosError>(
    (request) => postScenarioApply(String(params.scenarioId), request)
  );

  const appliedScenarioQuery = useGet(
    ['getScenarioAppliedFeedProgramsOverview', params.scenarioId],
    ({ queryKey }) => getScenarioAppliedFeedProgramsOverview(String(queryKey[1])),
    {
      enabled: !!params.scenarioId && params.subTab === ScenarioSubTab.APPLIED,
    }
  );

  return (
    <ScenarioFishGroupsContext.Provider
      value={{
        cageFeedPrograms,
        feedMappings,
        setCageFeedPrograms,
        setFeedMappings,
        applyMutation,
        appliedScenarioQuery,
      }}
    >
      {children}
    </ScenarioFishGroupsContext.Provider>
  );
};

function useScenarioFishGroupsState() {
  const context = useContext(ScenarioFishGroupsContext);
  if (!context) throw new Error('useScenarioFishGroupsState must be used within ScenarioFishGroupsProvider');
  return context;
}

export { ScenarioFishGroupsProvider, useScenarioFishGroupsState };
